import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, PanInfo, useReducedMotion } from 'framer-motion';
import { Grabber, Panel, Scrim, Title } from './styles';

interface IBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

/** Spring de gaveta: sem overshoot visível, assenta rápido. */
const SHEET_SPRING = { type: 'spring', bounce: 0.15, duration: 0.35 } as const;

/** Distância (px) ou velocidade (px/s) de arraste que fecha o sheet. */
const CLOSE_OFFSET = 120;
const CLOSE_VELOCITY = 600;

/**
 * Painel que sobe da base da tela, para o celular. Fecha arrastando para
 * baixo, tocando fora ou com Esc; desce pelo mesmo caminho por onde subiu.
 */
const BottomSheet: React.FC<IBottomSheetProps> = ({
  open,
  onClose,
  title,
  children,
}) => {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > CLOSE_OFFSET || info.velocity.y > CLOSE_VELOCITY) {
      onClose();
    }
  };

  const hidden = reduceMotion ? { opacity: 0 } : { y: '100%' };
  const shown = reduceMotion ? { opacity: 1 } : { y: 0 };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <Scrim
            key="scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <Panel
            key="panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            initial={hidden}
            animate={shown}
            exit={hidden}
            transition={reduceMotion ? { duration: 0.2 } : SHEET_SPRING}
            drag={reduceMotion ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.7 }}
            onDragEnd={handleDragEnd}
          >
            <Grabber aria-hidden />
            <Title>{title}</Title>
            {children}
          </Panel>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default BottomSheet;
